'use client';

import { useState, type FormEvent } from 'react';
import { HoneypotField } from '@/components/honeypot-field';
import { FIELD_BASE } from '@/lib/form-styles';
import { HONEYPOT_FIELD } from '@/lib/forms/spam';
import { readTrackingPayload } from '@/lib/forms/tracking-payload';
import {
  COLLABORATION_TYPES,
  CONTRIBUTE_FIELDS as F,
  CONTRIBUTE_FORM_ENDPOINT,
  MIN_PITCH_CHARS,
} from '@/lib/forms/contribute';

const STATUS = {
  idle: 'idle',
  submitting: 'submitting',
  done: 'done',
  error: 'error',
} as const;
type Status = (typeof STATUS)[keyof typeof STATUS];

const LABEL = 'text-[13px] font-semibold text-brand-navy/80';
const HINT = 'text-xs text-brand-navy/70';

function Label({ htmlFor, children, required }: { htmlFor: string; children: string; required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className={LABEL}>
      {children}
      {required ? <span className="text-red-600"> *</span> : null}
    </label>
  );
}

/** Contributor and partnership pitch form; confirms inline, never redirects. */
export function ContributeForm() {
  const [status, setStatus] = useState<Status>(STATUS.idle);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(STATUS.submitting);
    setErrorMessage(undefined);

    const data = new FormData(event.currentTarget);
    try {
      const response = await fetch(CONTRIBUTE_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          firstName: data.get(F.firstName),
          lastName: data.get(F.lastName),
          email: data.get(F.email),
          company: data.get(F.company),
          siteUrl: data.get(F.siteUrl),
          collaborationType: data.get(F.collaborationType),
          topic: data.get(F.topic),
          pitch: data.get(F.pitch),
          samples: data.get(F.samples),
          [HONEYPOT_FIELD]: data.get(HONEYPOT_FIELD) || undefined,
          tracking: readTrackingPayload(),
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { error?: string; issues?: Record<string, string[] | undefined> }
          | null;
        const firstIssue = Object.values(body?.issues ?? {}).flat()[0];
        throw new Error(firstIssue ?? body?.error ?? 'Something went wrong');
      }
      setStatus(STATUS.done);
    } catch (error) {
      setStatus(STATUS.error);
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong');
    }
  }

  if (status === STATUS.done) {
    return (
      <div role="status" className="rounded-xl bg-brand-mist p-6">
        <p className="text-lg font-bold text-brand-royal">Thanks, your pitch is in.</p>
        <p className="mt-2 text-[15px] leading-relaxed text-brand-navy/75">
          Our marketing team reads every one. If it fits the blog, we&rsquo;ll email you with next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.firstName} required>First name</Label>
          <input id={F.firstName} name={F.firstName} required autoComplete="given-name" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.lastName} required>Last name</Label>
          <input id={F.lastName} name={F.lastName} required autoComplete="family-name" className={FIELD_BASE} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.email} required>Email</Label>
        <input id={F.email} name={F.email} type="email" required autoComplete="email" className={FIELD_BASE} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.company}>Company or publication</Label>
          <input id={F.company} name={F.company} autoComplete="organization" className={FIELD_BASE} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={F.siteUrl}>Your website</Label>
          <input id={F.siteUrl} name={F.siteUrl} type="url" placeholder="https://" autoComplete="url" className={FIELD_BASE} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.collaborationType} required>What would you like to do?</Label>
        <select
          id={F.collaborationType}
          name={F.collaborationType}
          required
          defaultValue={COLLABORATION_TYPES[0]}
          className={FIELD_BASE}
        >
          {COLLABORATION_TYPES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.topic} required>Topic or working headline</Label>
        <input id={F.topic} name={F.topic} required maxLength={200} className={FIELD_BASE} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.pitch} required>Your pitch</Label>
        <textarea
          id={F.pitch}
          name={F.pitch}
          required
          minLength={MIN_PITCH_CHARS}
          rows={6}
          aria-describedby={`${F.pitch}-hint`}
          className={FIELD_BASE}
        />
        <p id={`${F.pitch}-hint`} className={HINT}>
          A few sentences: the angle, who it helps, and why you are the person to write it.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={F.samples}>Links to your published work</Label>
        <textarea id={F.samples} name={F.samples} rows={2} maxLength={1000} className={FIELD_BASE} />
      </div>
      <HoneypotField />
      {status === STATUS.error ? (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errorMessage}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={status === STATUS.submitting}
        className="self-start rounded-full bg-brand-yellow px-8 py-3.5 text-[15px] font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark hover:shadow-md disabled:translate-y-0 disabled:opacity-60"
      >
        {status === STATUS.submitting ? 'Sending…' : 'Send pitch'}
      </button>
    </form>
  );
}
